import { DocumentModel } from 'shared/domain/document/documentModel';
import { createInspectionScopedDocumentPath } from 'shared/domain/document/inspectionDocumentUrlPaths';
import { ISendDocumentUrlBuilder } from 'serviceWorker/repository/document/sendDocumentUrlBuilder';

class SendDocumentUrlBuilder implements ISendDocumentUrlBuilder {
  execute(document: DocumentModel, projectId: string): Promise<string> {
    if (document.issueId) {
      return this.buildIssueUrl(document, projectId);
    }
    if (document.inspectionId) {
      return this.buildInspectionUrl(document, projectId);
    }
    if (document.templateId) {
      return this.buildTemplateUrl(document, projectId);
    }
    return Promise.reject(new Error('Not implemented.'));
  }

  /**
   * The issue is named by the document's own issue id (the issue's server id),
   * so sending needs no stored issue: a document waiting in the queue goes
   * also after the issues were cleared (a repaired table).
   */
  private async buildIssueUrl(
    document: DocumentModel,
    projectId: string
  ): Promise<string> {
    return `/project/${projectId}${this.createIssueUrlPart(
      document.issueId,
      document.eventId
    )}${this.createDocumentUrlPart(document)}`;
  }

  private async buildTemplateUrl(
    document: DocumentModel,
    projectId: string
  ): Promise<string> {
    return `/project/${projectId}${this.createTemplateUrlPart(
      document
    )}${this.createChecklistUrlPart(document)}${this.createSectionUrlPart(
      document
    )}${this.createDocumentUrlPart(document)}`;
  }

  private async buildInspectionUrl(
    document: DocumentModel,
    projectId: string
  ): Promise<string> {
    if (document.templateId || document.protocolId || document.sectionId) {
      return `/project/${projectId}${createInspectionScopedDocumentPath(document)}`;
    }

    return `/project/${projectId}${this.createInspectionUrlPart(
      document
    )}${this.createDocumentUrlPart(document)}`;
  }

  private createDocumentUrlPart(document: DocumentModel): string {
    return document._id ? `/document/${document._id}` : '/document';
  }

  private createIssueUrlPart(
    issueId: string,
    eventId: string | undefined
  ): string {
    return `/issue/${issueId}${this.createEventUrlPart(eventId)}`;
  }

  private createEventUrlPart(eventId: string | undefined): string {
    return eventId ? `/event/${eventId}` : '';
  }

  private createTemplateUrlPart(document: DocumentModel): string {
    const templateUrl = `/inspectionTemplate/${document.templateId}`;
    if (document.checklistId || document.sectionId) {
      return templateUrl;
    }
    return `${templateUrl}/generalNorm`;
  }

  private createChecklistUrlPart(document: DocumentModel): string {
    return document.checklistId
      ? `/checklistItem/${document.checklistId}/norm`
      : '';
  }

  private createSectionUrlPart(document: DocumentModel): string {
    return document.sectionId ? `/section/${document.sectionId}` : '';
  }

  private createInspectionUrlPart(document: DocumentModel): string {
    return `/inspection/${document.inspectionId}`;
  }
}

export const sendDocumentUrlBuilder = new SendDocumentUrlBuilder();
